import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';

import type { NoChildrenProp, RefElement, WithAttributes } from '../../types';
import { useConfiguration } from '../../hooks';
import NoValue from '../NoValue/NoValue';

import { getFraction, getFormattedValue } from './utils';

export type NumberDisplayProps = WithAttributes<
  'span',
  NoChildrenProp & {
    /** Number value */
    value?: number | bigint;
    /** Unit to be included in the text, for example '%' */
    unit?: string;

    formattingOptions?: {
      /**
       * Value display notation.
       * @default 'standard'
       */
      notation?: 'standard' | 'compact';
      /**
       * Determines the fixed number of fraction digits. The value is rounded to the given precision.
       * Maximum is 20, default is value precision.
       * @default variable
       */
      fractionDigits?: number;
      /**
       * Determines whether group separators should be shown.
       * @default true
       */
      groupSeparators?: boolean;
    };
  }
>;

export default forwardRef<RefElement<NumberDisplayProps>, PropsWithoutRef<NumberDisplayProps>>(
  function NumberDisplay(
    {
      value,
      unit = '',
      formattingOptions: {
        fractionDigits = getFraction(String(value)).substring(1).length,
        notation,
        groupSeparators = true
      } = {},
      ...restProps
    },
    ref
  ) {
    const { locale } = useConfiguration();

    return (
      <span ref={ref} {...restProps}>
        {value || value === 0 ? (
          getFormattedValue(value.toString(), locale, unit, {
            notation,
            useGrouping: groupSeparators,
            maximumFractionDigits: fractionDigits,
            minimumFractionDigits: fractionDigits
          })
        ) : (
          <NoValue />
        )}
      </span>
    );
  }
);
